import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import useLearnProgress from "../../../hooks/useLearnProgress";
import { useProfile } from "../../../contexts/ProfileContext";
import { isWritingLessonComplete, resolveWritingPlayerRoute, writingLessonRoute, writingTownRoute } from "../../../utils/learnWritingProgress";
import LearnStage from "../shell/LearnStage";
import Celebration from "../feedback/Celebration";
import useLearnAudio from "../audio/useLearnAudio";
import { LEARN, usePrefersReducedMotion } from "../theme";
import { WRITING_ENGINE_MAP } from "./games";

const CHEERS = ["Beautiful writing!", "Look at that!", "You wrote it!", "Steady hand!", "Super!"];

function darken(hex, amount = 0.32) {
  const raw = String(hex || "#946EF2").replace("#", "");
  const n = parseInt(raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw, 16);
  const ch = (v) => Math.max(0, Math.round(v * (1 - amount))).toString(16).padStart(2, "0");
  return `#${ch((n >> 16) & 255)}${ch((n >> 8) & 255)}${ch(n & 255)}`;
}

export default function WritingLessonPlayer() {
  const navigate = useNavigate();
  const { townLessonId, activityId } = useParams();
  const { currentProfile } = useProfile();
  const { courses, loading, markWritingLessonComplete, markWritingTownComplete } = useLearnProgress();
  const audio = useLearnAudio();
  const reduceMotion = usePrefersReducedMotion();

  const resolved = resolveWritingPlayerRoute({ townLessonId, activityId, courses });
  const lesson = resolved.lesson;
  const activity = resolved.activity;
  const index = lesson?.activities.findIndex((a) => a.id === activity?.id) ?? -1;
  const next = index >= 0 ? lesson.activities[index + 1] : null;
  const Engine = activity ? WRITING_ENGINE_MAP[activity.engine] : null;
  const accent = activity?.accent || LEARN.course.writing.accent;
  const accentDeep = darken(accent);

  const [ready, setReady] = useState(false);
  const [pose, setPose] = useState("idle");
  const [cheer] = useState(() => CHEERS[Math.floor(Math.random() * CHEERS.length)]);
  const wroteRef = useRef(false);

  useEffect(() => {
    wroteRef.current = false;
    setReady(false);
    setPose("idle");
    audio.cancel();
    if (!activity) return undefined;
    const line = activity.intro || activity.instruction;
    audio.prefetch([line, next?.intro || next?.instruction].filter(Boolean));
    audio.sfx("pageturn", { volume: 0.55 });
    const t = setTimeout(() => audio.speak(line, { interrupt: true }), 350);
    return () => { clearTimeout(t); audio.cancel(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?.id]);

  const goTown = useCallback(() => {
    audio.cancel();
    navigate(writingTownRoute());
  }, [audio, navigate]);

  const handleComplete = useCallback(async () => {
    if (!activity || !lesson || ready) return;
    setPose("cheer");
    audio.cancel();
    audio.sfx(next ? "sparkle" : "levelup");
    if (!wroteRef.current) {
      wroteRef.current = true;
      await markWritingLessonComplete(lesson.lessonNumber, activity.id);
    }
    const key = `writing.lesson${lesson.lessonNumber}`;
    const nextCourses = { ...courses, [key]: [...new Set([...(courses[key] || []), activity.id])] };
    if (!next && isWritingLessonComplete(lesson, nextCourses)) await markWritingTownComplete(lesson.townId);
    setReady(true);
  }, [activity, audio, courses, lesson, markWritingLessonComplete, markWritingTownComplete, next, ready]);

  const advance = useCallback(() => {
    setReady(false);
    if (next) {
      navigate(writingLessonRoute(lesson.townId, next.id), { replace: true });
      return;
    }
    goTown();
  }, [goTown, lesson, navigate, next]);

  if (!loading && resolved.redirectTo) return <Navigate to={resolved.redirectTo} replace />;
  if (!lesson || !activity) return <LearnStage title="Writing" tone="light" accent={accent} accentDeep={accentDeep} onClose={goTown} />;

  return (
    <>
      <LearnStage
        tone="light"
        background="writing_town_path_bg_wide"
        title={activity.title}
        subtitle={`${lesson.title} · ${index + 1} of ${lesson.activities.length}`}
        prompt={activity.instruction}
        accent={accent}
        accentDeep={accentDeep}
        progress={{ done: index + (ready ? 1 : 0), total: lesson.activities.length }}
        companionPose={pose}
        onClose={goTown}
        onReplayPrompt={() => audio.replay(activity.intro || activity.instruction)}
        actionLabel={next ? "Next" : "Done"}
        actionEnabled={ready}
        onAction={advance}
        speaking={audio.speaking}
      >
        {Engine ? (
          <Engine
            key={activity.id}
            lesson={lesson}
            activity={activity}
            profile={currentProfile}
            speak={audio.speak}
            replay={audio.replay}
            sfx={audio.sfx}
            reduceMotion={reduceMotion}
            onComplete={handleComplete}
            onWrong={() => { setPose("oops"); audio.sfx("wrong", { volume: 0.6 }); }}
          />
        ) : (
          <div style={{ padding: 24 }}>Missing engine {activity.engine}</div>
        )}
      </LearnStage>

      <Celebration
        open={ready}
        kind={next ? "star" : "ribbon"}
        title={next ? cheer : `${lesson.title} complete!`}
        detail={next ? `Next: ${next.title}` : "The next stop in Writing Town just unlocked."}
        cta={next ? "Next" : "Back to town"}
        onCta={advance}
        reduceMotion={reduceMotion}
      />
    </>
  );
}
